import { memo } from "react";

import { AdminSelect } from "@/components/Selects/adminSelect";
import { LandingPageButtons } from "@/features/campaigns/components/landingPage/LandingPageButtons";
import CustomSelect from "components/Forms/CustomSelect";
import { titleCaseFormatter } from "helpers/formatters";
import BrandSelect from "modules/Brands/components/BrandSelect";
import CampaignStatusBadge from "modules/Campaigns/components/CampaignStatusBadge";

const CampaignStatusInput = (props) => (
  <CustomSelect
    {...props}
    name="status"
    // TODO: default to waiting
    options={[
      // TODO: use unified campaign status enum dict
      { value: "active", label: "Active" },
      { value: "completed", label: "Completed" },
      { value: "paused", label: "Paused" },
      { value: "waiting", label: "Waiting" },
    ]}
  />
);

const DepartmentInput = (props) => (
  <CustomSelect
    {...props}
    name="department"
    options={[
      { value: "managed", label: "Managed Services" },
      { value: "talent", label: "Talent" },
    ]}
  />
);

export const campaignDataDefs = (
  displayedCampaign,
  { canMakePayment = false } = {},
) => {
  return {
    header: {
      type: "string",
      label: "Campaign",
      value: displayedCampaign?.name,
    },
    body: [
      {
        role: "section",
        fields: [
          {
            label: "Name",
            fieldKey: "name",
            type: "string",
            creatorVisible: true,
            hidden: true,
            editing: {
              required: true,
              hidden: false,
            },
          },
          {
            label: "Campaign Logo",
            fieldKey: "logoFile",
            type: "inline-uploader",
            hidden: true,
            editing: {
              creatorVisible: true,
              required: false,
              hidden: false,
              fieldInputProps: {
                accept: "image/*",
                multiple: false,
                existingImageUrl: displayedCampaign?.logoFileUrl,
              },
            },
          },
          {
            label: "Campaign ID",
            fieldKey: "_id",
            type: "id",
            editing: false,
          },
          {
            label: "Campaign Logo",
            fieldKey: "logoFileUrl",
            type: "inline-uploader",
            hidden: false,
            editing: {
              hidden: true,
            },
          },
          {
            label: "Status",
            value: displayedCampaign?.status,
            fieldKey: "status",
            type: "select",
            CustomRenderer: memo(CampaignStatusBadge),
            editing: {
              required: true,
              CustomRenderer: memo(CampaignStatusInput),
            },
          },
          {
            label: "Campaign Owner",
            fieldKey: "__campaignOwner.name",
            type: "string",
            // displayValue: displayedCampaign?.__campaignOwner?.name,
            // componentProps: {
            //   value: displayedCampaign?.__campaignOwner?.name,
            // },
            editing: {
              CustomRenderer: memo(AdminSelect),
              fieldKey: "campaignOwnerAdmin_id",
              defaultValue: null,
              type: "custom",
              required: true,
              // label: null,
              // creatorVisible: null,
              // componentProps: { label: "Campaign Owner" },
              // fieldKey: "owner_id",
            },
          },
          {
            label: "Brand",
            fieldKey: "brand_id?._id",
            displayValue: displayedCampaign?.brand_id?.name,
            type: "string",
            // CustomRenderer: memo(BrandLink),
            // componentProps: {
            //   _id: displayedCampaign?.brand_id?._id,
            //   data: displayedCampaign,
            // },
            editing: {
              required: true,
              fieldKey: "brand_id",
              disableCondition: () => {
                return (
                  displayedCampaign?.shortId
                  || displayedCampaign?.enableAccountSegmentation
                );
              },
              CustomRenderer: memo(BrandSelect),
              componentProps: {},
            },
          },
          {
            label: "Department",
            fieldKey: "department",
            type: "select",
            displayValue:
              displayedCampaign?.department === "managed"
                ? "Managed Services"
                : titleCaseFormatter(displayedCampaign?.department),
            editing: {
              disableCondition: () => {
                return (
                  displayedCampaign?.shortId
                  || displayedCampaign?.enableAccountSegmentation
                  || displayedCampaign?.department
                );
              },
              // should be required on all new campaigns. For existing campaigns, required if account segmentation is enabled, otherwise optional
              required:
                !displayedCampaign?._id
                || displayedCampaign?.enableAccountSegmentation,
              CustomRenderer: memo(DepartmentInput),
            },
          },
          {
            label: "Account Segmentation",
            fieldKey: "enableAccountSegmentation",
            type: "switch",
            editing: {
              label: "Account Segmentation",
              defaultValue: false,
              disableCondition: () => {
                // * backend validating for not being able to change this on legacy campaigns (created before segmentation features were released)
                // Allow users with payment permissions to disable account segmentation
                return (
                  displayedCampaign?.enableAccountSegmentation
                  && !canMakePayment
                );
              },
            },
          },
          {
            label: displayedCampaign?._id ? "Short ID" : "Short Code",
            fieldKey: "shortCode",
            displayValue: displayedCampaign?.shortId,
            type: "id",
            description:
              "A unique abbreviation indicating the Department, Brand, and Campaign.",
            editing: {
              description: displayedCampaign?.shortId
                ? "A unique abbreviation indicating the Department, Brand, and Campaign."
                : "A 3 character abbreviation for the campaign. Must be unique across a given brand's campaigns. This will be appended to the Department and Brand abbreviations to form the full Short ID.",
              // type: "custom",
              // customType: "text",
              // CustomRenderer: memo(CampaignShortCodeInput),
              disableCondition: (values) => {
                return (
                  displayedCampaign?.shortId
                  || !values?.department
                  || !values?.brand_id
                );
              },
              hideCondition: (values) => {
                return (
                  !values?.enableAccountSegmentation
                  || displayedCampaign?.shortId
                );
              },
            },
          },
          {
            label: "Landing Page",
            fieldKey: "landingPageEditor",
            type: "custom",
            CustomRenderer: memo(LandingPageButtons),
            value: displayedCampaign?._id,
            editing: false,
          },
          {
            label: "Enable Landing Page",
            fieldKey: "enableCampaignLandingPage",
            type: "switch",
            editing: true,
            // TODO #campaignLanding: add disable condition for when no landing page has been created
          },
          {
            label: "Enable Addresses and Shipping Feature",
            fieldKey: "isShippingCampaign",
            type: "switch",
            editing: {
              defaultValue: false,
            },
          },
          {
            label: "Client Login Allowed",
            fieldKey: "allowClientAccess",
            type: "switch",
            editing: true,
          },
          {
            label: "Start Date",
            fieldKey: "startDate",
            type: "date",
            editing: true,
          },
          {
            label: "End Date",
            fieldKey: "endDate",
            type: "date",
            editing: true,
          },
          {
            label: "Created Date",
            fieldKey: "created",
            type: "date",
            editing: false,
          },
          {
            label: "Creators",
            value: displayedCampaign?.__assignmentBalances?.count,
            type: "number",
            editing: false,
          },
        ],
      },
      {
        role: "section",
        label: "Balances & Rates",
        fields: [
          {
            label: "SOW Amount",
            type: "currency",
            fieldKey: "sowAmount",
            editing: true,
          },
          {
            label: "Invoiced",
            type: "currency",
            fieldKey: "invoicedAmount",
            hideCondition: (values) => {
              return !values?.enableAccountSegmentation;
            },
          },
          {
            label: "Funded",
            type: "currency",
            fieldKey: "fundedAmount",
            hideCondition: (values) => {
              return !values?.enableAccountSegmentation;
            },
          },
          {
            label: "Segmentation Split (%)",
            type: "percentage",
            fieldKey: "segmentedNetPercent",
            description: "The net percentage take that goes to NeoReach",
            editing: true,
          },
          {
            label: "Allocated",
            fieldKey: "__assignmentBalances.allocatedBalance",
            type: "currency",
          },
          {
            label: "Unallocated",
            fieldKey: "__assignmentBalances.unallocatedBalance",
            type: "currency",
          },
          {
            label: "Creator Budget",
            type: "currency",
            fieldKey: "creatorBudget",
            editing: true,
          },
          {
            label: "Paid to Creators",
            type: "currency",
            value: displayedCampaign?.amountPaidToCreators,
          },
          {
            label: "Hold",
            value: displayedCampaign?.__assignmentBalances?.holdBalance,
            type: "currency",
          },
        ],
      },
    ],
  };
};
